import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import {
  ArrayField,
  Datagrid,
  Error,
  FunctionField,
  Loading,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, makeStyles } from '@material-ui/core';

import ExpandableStudentQuizTable from 'resources/common/ExpandableStudentQuizTable';
import { OfflineList } from 'resources/common';
import {
  getQuizModuleName,
  getQuizSubmoduleName,
  getQuizUnitName,
} from 'resources/common/quizProgress/quizPathUtils';
import { filterQuizForStudent } from 'resources/common/quizProgress/studentMatch';

const useStyles = makeStyles({
  summaryCell: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
    color: '#757575',
    fontSize: 13,
  },
});

const formatModuleProgress = (moduleRecord) => {
  const quizzes = R.propOr([], 'quizzes', moduleRecord);
  const submittedCount = R.pipe(
    R.filter(R.prop('student_submission')),
    R.length
  )(quizzes);
  return `${submittedCount}/${R.length(quizzes)}`;
};

const QuizList = ({ student, course }) => {
  const classes = useStyles();
  const [quizzes, setQuizzes] = useState([]);

  const {
    data: apiQuizzes,
    loading: quizzesLoading,
    error: quizzesError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'quizzes',
    payload: {
      target: 'cohort',
      id: student.cohort,
      pagination: {
        perPage: 100,
      },
      filter: R.reject(R.isNil, { course, submissions: true }),
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  useEffect(() => {
    if (!apiQuizzes) return;
    setQuizzes(R.map(filterQuizForStudent(student), apiQuizzes));
  }, [apiQuizzes, student]);

  if (quizzesLoading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (quizzesError)
    return (
      <Box p="16px">
        <Error error={quizzesError} errorInfo={quizzesError} />
      </Box>
    );

  if (R.isEmpty(quizzes)) {
    return null;
  }

  const modules = R.pipe(
    R.groupBy(getQuizModuleName),
    R.toPairs,
    R.map(([moduleName, moduleQuizzes]) => ({
      id: moduleName,
      display_name: moduleName,
      quizzes: moduleQuizzes,
      submodules: R.pipe(
        R.groupBy(getQuizSubmoduleName),
        R.toPairs,
        R.map(([submoduleName, submoduleQuizzes]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          quizzes: submoduleQuizzes,
          units: R.pipe(
            R.groupBy(getQuizUnitName),
            R.toPairs,
            R.map(([unitName, unitQuizzes]) => ({
              id: `${moduleName}::${submoduleName}::${unitName}`,
              display_name: unitName,
              quizzes: unitQuizzes,
            }))
          )(submoduleQuizzes),
        })),
        R.reverse
      )(moduleQuizzes),
    })),
    R.reverse
  )(quizzes);

  const ModuleQuizzesTable = ({ record }) => {
    const unitQuizzes = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'units')),
      R.chain(R.propOr([], 'quizzes')),
      R.sortBy(getQuizUnitName)
    )(record);
    if (R.isEmpty(unitQuizzes)) return null;
    return (
      <OfflineList
        list={unitQuizzes}
        uniqueKey="id"
        defaultSort={{ field: 'path.2.display_name', order: 'ASC' }}
        sorters={{
          student_score: R.prop('student_score'),
        }}
        nullsLastFields={['student_score']}
      >
        <ExpandableStudentQuizTable />
      </OfflineList>
    );
  };

  return (
    <ArrayField source="modules" record={{ modules }}>
      <Datagrid rowClick="expand" expand={<ModuleQuizzesTable />}>
        <TextField label="Module" source="display_name" sortable={false} />
        <FunctionField
          label=""
          sortable={false}
          textAlign="right"
          cellClassName={classes.summaryCell}
          headerClassName={classes.summaryCell}
          render={formatModuleProgress}
        />
      </Datagrid>
    </ArrayField>
  );
};

const ByQuizzesSubtab = ({ record: student, course }) => {
  if (!student?.cohort) return null;
  return <QuizList student={student} course={course} />;
};

export default ByQuizzesSubtab;
